import { ObserveMultiState } from "../../../CommonUI/StateManagement/ObserveMultiState";
import { ILine } from "../../Structure/DisplayBehavior/ILine";
import { CloseSmallIcon } from "../../../CommonUI/Icon/CloseSmallIcon";
import { CandidateSequence } from "../../../rule-engine-core/Flow/Structure/CandidateSequence";
import styled from "styled-components";
import { FlowStepSequence } from "../../../rule-engine-core/Flow/Structure/FlowStepSequence";
import { FlowEditContext } from "../../FlowEditContext";
import { FlowConfiguration } from "../../../rule-engine-core/Flow/FlowConfiguration";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import Xarrow from "react-xarrows";
import { FlowUtilities } from "../../../rule-engine-core/Flow/Utilities/FlowUtilities";
import { BaseTestResultData } from "../../../rule-engine-core/Common/Testing/BaseTestResultData";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { FlowBranchTestData } from "../../../rule-engine-core/Flow/Structure/FlowTestingContext";

interface ConnectionLineProps {
    line: ILine;
    editContext: FlowEditContext;
    flow: FlowConfiguration;
}

export const ConnectionLine: React.FC<ConnectionLineProps> = (props: ConnectionLineProps) => {
    const { line } = props;

    return (
        <ObserveMultiState
            listenTo={[line.value.fromSequence, line.value.fromInstruction, line.value.fromStep, line.targetSequence]}
            control={() => {
                let targetSequence = line.targetSequence;
                return (
                    <TestContext.Consumer>
                        {(testcontext: BaseTestResultData | undefined) => {
                            let myContext = testcontext
                                ? testcontext.findItem(
                                      FlowBranchTestData.getLineId(
                                          props.line.value.fromId,
                                          props.line.value.toId,
                                          props.line.value.fromInstruction.pathName,
                                      ),
                                  )
                                : undefined;

                            return (
                                <Xarrow
                                    data-testid={`${line.value.fromStep._id}-${line.value.fromInstruction.pathName}`}
                                    lineColor={myContext ? UIStyleConstants.GREEN_COLOR : undefined}
                                    _debug={false}
                                    divContainerStyle={{ zIndex: 2 }}
                                    {...line.arrow}
                                    labels={
                                        line.labels.length > 0
                                            ? {
                                                  ["middle"]: (
                                                      <ArrowLabelsWrapper>
                                                          {line.labels.map((l, idx) => {
                                                              let value = l.value;
                                                              return (
                                                                  <ArrowLabelWrapper key={idx}>
                                                                      <ArrowLabel
                                                                          data-testid={`path_${value.fromStep._id}-${value.fromInstruction.pathName}`}
                                                                          onClick={(
                                                                              event: React.MouseEvent<HTMLDivElement>,
                                                                          ) => {
                                                                              let hidden = FlowUtilities.IsEndStep(
                                                                                  value.fromStep,
                                                                              );

                                                                              if (!hidden) {
                                                                                  // for step strategy
                                                                                  props.editContext.setSelectedStepStrategy(
                                                                                      event.currentTarget,
                                                                                      value.fromStep,
                                                                                  );
                                                                              } else if (value.fromStep.sequence) {
                                                                                  // for sequence strategy
                                                                                  props.editContext.setSelectedSequenceStrategy(
                                                                                      event.currentTarget,
                                                                                      value.fromStep.sequence,
                                                                                  );
                                                                              }
                                                                          }}
                                                                          isFlowConnection={l.isFlowConnection}
                                                                      >
                                                                          {l.label}
                                                                      </ArrowLabel>
                                                                      {targetSequence &&
                                                                          targetSequence instanceof
                                                                              FlowStepSequence && (
                                                                              <div
                                                                                  className="close-connection-div"
                                                                                  data-testid={`${value.fromStep._id}-${value.fromInstruction.pathName}_close-connection`}
                                                                                  onClick={() => {
                                                                                      let newCandidate =
                                                                                          new CandidateSequence(
                                                                                              value.fromInstruction,
                                                                                          );
                                                                                      value.fromInstruction.connectedSequenceId =
                                                                                          newCandidate._id;
                                                                                      props.editContext
                                                                                          .getCandidateSequences()
                                                                                          .push(newCandidate);
                                                                                      StateManager.propertyChanged(
                                                                                          targetSequence!,
                                                                                          FlowStepSequence.CONNECTION_CHANGED,
                                                                                      );
                                                                                  }}
                                                                              >
                                                                                  <CloseSmallIcon />
                                                                              </div>
                                                                          )}
                                                                  </ArrowLabelWrapper>
                                                              );
                                                          })}
                                                      </ArrowLabelsWrapper>
                                                  ),
                                              }
                                            : undefined
                                    }
                                />
                            );
                        }}
                    </TestContext.Consumer>
                );
            }}
        />
    );
};

export const ArrowLabelsWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

export const ArrowLabelWrapper = styled.div`
  position: relative;

  margin-top: 5px;

  &:first-child {
    margin-top: 0;
  }

  &:hover .close-connection-div {
    opacity: 1;
  }

  .close-connection-div {
    position: absolute;
    left: 100%;
    top: 50%;

    transform: translateY(-50%);
    height: 16px;
    width: 16px;

    background: ${UIStyleConstants.WHITE_COLOR};
    opacity: 0;
    margin-left: 5px;
    border: 1px
    solid: ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
    box-sizing: border-box;

    box-shadow: 0px 2px 4px -2px ${UIStyleConstants.GRAY_COLOR};
    border-radius: 4px;

    cursor: pointer;

    img {
      width: 100%;
    }
  }
`;

export const ArrowLabel = styled.div<{ isFlowConnection: boolean }>`
    padding: 2px 8px;
    font-size: 10px;

    font-style: normal;
    font-weight: 500;
    font-size: 10px;
    cursor: pointer;
    text-transform: capitalize;
    transition: all 0.1s ease-in-out;

    ${(props) =>
        !props.isFlowConnection
            ? `
        background: ${UIStyleConstants.BLUE_LIGHT_COLOR};
        color: ${UIStyleConstants.WHITE_COLOR};
        border-radius: 2px;
        line-height: 120%;

        &:hover {
            background: ${UIStyleConstants.BLUE_COLOR};
        }
    `
            : `
        border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
        box-sizing: border-box;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        background: ${UIStyleConstants.WHITE_COLOR};
        line-height: 140%;
        display: flex;
        align-items: center;
        
        box-shadow: 0px 2px 4px -2px ${UIStyleConstants.GRAY_COLOR};
        border-radius: 27px;

        &:hover {
            border: 1px solid ${UIStyleConstants.GRAY_DARK_COLOR};
            box-sizing: border-box;
            /* sh 4 2 4 */

            box-shadow: 0px 2px 4px -2px ${UIStyleConstants.GRAY_COLOR};
            border-radius: 11px;
        }
    `}
`;
